import { CommerceService } from "@/services/commerce"

import { DomainObjects, Operations, RolePermissions, Roles } from "../permissions/permissionsConfig"

const REGION_STAT_CONTAINER = "regionStatContainer"
const REGION_STATISTICS_ATTRIBUTE = "regionStatistics"
const CORPORATE_SANITY_ID = "region-Konzern"

/**
 * Whether the user may receive DB values for the cart
 *
 * @param {string} [role]
 * @returns {boolean}
 */
export const canAccessCartDbValues = (role) => {
  if (!role) return false
  if (role === Roles.ADMIN) return true

  return Boolean(RolePermissions[DomainObjects.CARTS]?.[Operations.READ]?.[role])
}

/**
 * @param {{ articleProjections: Array, skus: string[] }} params
 * @returns {Map<string, object>}
 */
const indexVariantsBySku = ({ articleProjections, skus }) => {
  const variantBySku = new Map()
  const targetSkus = new Set((skus || []).filter(Boolean))

  if (!articleProjections?.length || targetSkus.size === 0) {
    return variantBySku
  }

  for (const product of articleProjections) {
    const masterVariantSku = product?.masterVariant?.sku

    if (targetSkus.has(masterVariantSku) && !variantBySku.has(masterVariantSku)) {
      variantBySku.set(masterVariantSku, product.masterVariant)
    }

    if (variantBySku.size === targetSkus.size) break

    for (const variant of product?.variants || []) {
      const variantSku = variant?.sku

      if (targetSkus.has(variantSku) && !variantBySku.has(variantSku)) {
        variantBySku.set(variantSku, variant)
      }

      if (variantBySku.size === targetSkus.size) break
    }
  }

  return variantBySku
}

/**
 * @param {Array} attributes
 * @param {string} attributeName
 * @returns {any|null}
 */
const findVariantAttribute = ({ attributes, attributeName }) => {
  if (!attributes?.length) return null

  const targetAttribute = attributes.find((attribute) => attribute.name === attributeName)

  return targetAttribute?.value ?? null
}

/**
 * Extracts CommerceTools custom object ids from the variant `regionStatistics` set attribute
 *
 * @param {object|null} variant
 * @returns {string[]}
 */
const getRegionStatisticsRefIds = (variant) => {
  const attributeValue = findVariantAttribute({
    attributes: variant?.attributes,
    attributeName: REGION_STATISTICS_ATTRIBUTE,
  })

  if (!attributeValue || !Array.isArray(attributeValue)) {
    return []
  }

  return attributeValue.map((reference) => reference?.id).filter((id) => typeof id === "string" && id.length > 0)
}

/**
 * @param {string} value - Custom object `value` field (JSON string)
 * @returns {object|null}
 */
const parseRegionStatCustomObjectValue = (value) => {
  if (!value) return null

  try {
    let parsed = typeof value === "string" ? JSON.parse(value) : value

    if (typeof parsed === "string") {
      parsed = JSON.parse(parsed)
    }

    return parsed && typeof parsed === "object" ? parsed : null
  } catch (error) {
    console.warn("parseRegionStatCustomObjectValue()", { error })
    return null
  }
}

/**
 * Batch-loads region statistics custom objects by id.
 *
 * @param {string[]} ids
 * @returns {Promise<Map<string, { regionSanityId: string, avgDb: number|null, medDb: number|null }>>}
 */
const fetchRegionStatisticsByIds = async (ids) => {
  const statisticsById = new Map()

  if (!ids?.length) return statisticsById

  const uniqueIds = [...new Set(ids)]
  const commerceApi = CommerceService.api()
  const limit = 500
  let offset = 0

  do {
    const batchIds = uniqueIds.slice(offset, offset + limit).map((id) => `"${id}"`)

    if (batchIds.length === 0) break

    const response = (
      await commerceApi
        .customObjects()
        .withContainer({ container: REGION_STAT_CONTAINER })
        .get({ queryArgs: { where: `id in (${batchIds.join(",")})`, limit } })
        .execute()
    )?.body?.results

    for (const customObject of response || []) {
      const parsed = parseRegionStatCustomObjectValue(customObject?.value)

      if (parsed?.regionSanityId) {
        statisticsById.set(customObject.id, {
          regionSanityId: parsed.regionSanityId,
          avgDb: Number(parsed.avgDb) ?? null,
          medDb: Number(parsed.medDb) ?? null,
        })
      }
    }

    offset += limit
  } while (offset < uniqueIds.length)

  return statisticsById
}

/**
 * Returns the highest value between two values
 *
 * @param {number|null} value1
 * @param {number|null} value2
 * @returns {number|null}
 */
const getHighestValue = (value1, value2) => {
  if (value1 == null) return value2 ?? null
  if (value2 == null) return value1
  return Math.max(Number(value1), Number(value2))
}

/**
 * Picks the DB comparison value for one SKU from loaded custom objects.
 *
 * @param {{ refIds: string[], statisticsById: Map<string, object>, regionSanityId: string }} params
 * @returns {number|null}
 */
const getDbComparisonValue = ({ refIds, statisticsById, regionSanityId }) => {
  if (!regionSanityId || !refIds?.length) return null

  let regionStatistics = null
  let corporateStatistics = null

  for (const refId of refIds) {
    const statistics = statisticsById.get(refId)

    if (!regionStatistics && statistics?.regionSanityId === regionSanityId) {
      regionStatistics = statistics
    }

    if (!corporateStatistics && statistics?.regionSanityId === CORPORATE_SANITY_ID) {
      corporateStatistics = statistics
    }

    if (regionStatistics && corporateStatistics) {
      break
    }
  }

  const regionDb = getHighestValue(regionStatistics?.avgDb ?? null, regionStatistics?.medDb ?? null)
  const corporateDb = getHighestValue(corporateStatistics?.avgDb ?? null, corporateStatistics?.medDb ?? null)

  return getHighestValue(regionDb, corporateDb)
}

/**
 * Collects regionStatistics reference ids for a set of SKUs from article projections
 *
 * @param {{ articleProjections: Array, skus: string[] }} params
 * @returns {{ refIdsBySku: Map<string, string[]>, allRefIds: string[] }}
 */
const collectRegionStatisticsRefIds = ({ articleProjections, skus }) => {
  const refIdsBySku = new Map()
  const allRefIds = new Set()

  if (!articleProjections?.length || !skus?.length) {
    return { refIdsBySku, allRefIds: [] }
  }

  const variantBySku = indexVariantsBySku({ articleProjections, skus })
  const uniqueSkus = [...new Set(skus.filter(Boolean))]

  for (const sku of uniqueSkus) {
    const refIds = getRegionStatisticsRefIds(variantBySku.get(sku))
    if (refIds.length > 0) {
      refIdsBySku.set(sku, refIds)

      for (const refId of refIds) {
        allRefIds.add(refId)
      }
    }
  }

  return { refIdsBySku, allRefIds: [...allRefIds] }
}

/**
 * Loads the average DB value for SKUs for the given Sanity region ID
 *
 * @param {{ articleProjections: Array, skus: string[], regionSanityId: string }} params
 * @returns {Promise<Map<string, number>>}
 */
export const getAvgDbBySku = async ({ articleProjections, skus, regionSanityId }) => {
  const avgDbBySku = new Map()

  if (!regionSanityId || !skus?.length) {
    return avgDbBySku
  }

  const { refIdsBySku, allRefIds } = collectRegionStatisticsRefIds({ articleProjections, skus })

  if (allRefIds.length === 0) {
    return avgDbBySku
  }

  const statisticsById = await fetchRegionStatisticsByIds(allRefIds)

  for (const [sku, refIds] of refIdsBySku) {
    const avgDb = getDbComparisonValue({ refIds, statisticsById, regionSanityId })

    if (avgDb !== null) {
      avgDbBySku.set(sku, avgDb)
    }
  }

  return avgDbBySku
}
